// KPI-ASSIGN queries: a seat's stored metric set (`GET seats/{id}/metrics`, the versioned `seats.metrics`), its target rules,
// and every metric definition (all pages: pickers search the whole catalog). Keys sit under
// `["company", co]`, so every existing KPI invalidation also refreshes them.
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { Api } from "../../api/client";
import type { MetricDef } from "../../api/types";
import { keys, metricsQuery, seatMetricSetQuery } from "../queries";

export const kpiKeys = {
  seatSet: keys.seatMetricSet,
  seatTargets: (co: string, seat: string) => [...keys.targets(co), "seat", seat] as const,
};

/** The versioned set (`seats.metrics`, KPI-FIX's read): the one-row writes send its version. */
export const seatSetQuery = seatMetricSetQuery;

/** The seat's own target rules (a company rule is the fallback the scorecard resolves). */
export const seatTargetsQuery = (api: Api, co: string, seat: string) => ({
  queryKey: kpiKeys.seatTargets(co, seat),
  queryFn: () =>
    api.client.targets.list({
      params: { co },
      query: { subject_kind: "seat", subject_id: seat, limit: 200 },
    }),
});

/** Every metric definition: follows `next_cursor` until the catalog is complete. */
export function useAllMetrics(api: Api, co: string, enabled = true) {
  const q = useInfiniteQuery({ ...metricsQuery(api, co), enabled });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const all: MetricDef[] = q.data?.pages.flatMap((p) => p.data) ?? [];
  return { ...q, all, complete: q.isSuccess && !hasNextPage };
}
